import React, { useEffect, useRef, useState } from "react";
import { useTabActive } from "../hook/useTabActive";
import { SideBarTabs } from "../constants/tabs";
import { CommonStyles } from "styles/CommonStyles";
import TodayStatCard from "components/card/TodayStatCard";
import AllTimeStatCard from "components/card/AllTimeStatCard";
import AppCopy from "copy/AppCopy";
import LiveCarStatusHeader from "components/header/LiveCarStatusHeader";
import LiveCarCard from "components/card/LiveCarCard";
import Menu from "components/common/Menu";
import useCars from "hook/useCars";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import useAdminOnly from "hook/useAdminOnly";
import useStat from "hook/useStat";
import SearchBar from "components/common/SearchBar";
import PageHeader from "components/common/PageHeader";
import EmptyState from "components/common/EmptyState";
import LoadMoreButton from "components/common/LoadMoreButton";
import { SocketEvents } from "config/socket";
import { getSocketClient, subscribeToSocketEvent } from "utils/socketClient";
import useReport from "hook/useReport";
import CallStatusCard from "components/card/CallStatusCard";

function HomePage() {
  const { getLiveCars } = useCars();
  const { getTodayStat, getTotalStat } = useStat();
  const { getClicks } = useReport();
  const { liveCars } = useSelector((state: RootState) => state.car);
  const { clicks } = useSelector((state: RootState) => state.report);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loadingMore, setloadingMore] = useState<boolean>(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const searchRef = useRef("");
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getLiveCarsRef = useRef(getLiveCars);

  useAdminOnly();

  const loadData = async () => {
    const promises = [getLiveCars({}, {}), getTodayStat({}), getTotalStat({}), getClicks({ limit: 8 }, { reload: true })];
    await Promise.all(promises);
  };

  useTabActive(SideBarTabs.Overview.name);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    searchRef.current = searchValue;
  }, [searchValue]);

  useEffect(() => {
    getLiveCarsRef.current = getLiveCars;
  }, [getLiveCars]);

  useEffect(() => {
    const refreshLiveStatus = () => {
      void getLiveCarsRef.current(
        { search: searchRef.current.trim() },
        { reload: true }
      );
    };
    const scheduleRefresh = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(refreshLiveStatus, 200);
    };
    const unsubscribe = subscribeToSocketEvent(
      SocketEvents.CarChanged,
      scheduleRefresh
    );
    const socket = getSocketClient();
    setLiveConnected(socket.connected);
    const unsubscribeConnected = subscribeToSocketEvent("connect", () => {
      setLiveConnected(true);
      scheduleRefresh();
    });
    const unsubscribeDisconnected = subscribeToSocketEvent("disconnect", () => {
      setLiveConnected(false);
    });
    const interval = window.setInterval(refreshLiveStatus, 30_000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshLiveStatus();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      unsubscribe();
      unsubscribeConnected();
      unsubscribeDisconnected();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const handleLoadMore = async () => {
    await getLiveCars(
      {
        offset: liveCars.length,
        search: searchValue.trim(),
        setLoading: setloadingMore,
      },
      { more: true }
    );
  };

  const handleSearch = () => {
    getLiveCars({ search: searchValue.trim() }, { reload: true });
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const { todayTitleText } = AppCopy.stat;
  const { searchPlaceholder, liveCarTitle, liveText, refreshingText } =
    AppCopy.overview;

  return (
    <div className={`${CommonStyles.pageWrapper}`}>
      <Menu />
      <PageHeader
        title="Dashboard"
        description="Review today's activity and active listings."
      />
      <div className={ClassName.container}>
        <section className={ClassName.stat}>
          <span className={ClassName.statTitle}>{todayTitleText}</span>
          <div className={ClassName.statCards}>
            <TodayStatCard />
            <AllTimeStatCard />
          </div>
        </section>
        <section className={ClassName.liveCarContainer}>
          <div className={ClassName.callActivity}>
            <div>
              <h2 className={ClassName.callTitle}>Recent buyer calls</h2>
              <p className={ClassName.callSubtitle}>Follow up quickly with buyers who tapped a listing’s call action.</p>
            </div>
            <div className={ClassName.callGrid}>
              {clicks.slice(0, 8).map((click, index) => <CallStatusCard key={String(click._id || index)} click={click} />)}
              {!clicks.length ? <EmptyState title="No buyer calls yet" description="Recent call activity will appear here." /> : null}
            </div>
          </div>
          <SearchBar
            onChange={(event) => setSearchValue(event.target.value)}
            value={searchValue}
            placeholder={searchPlaceholder}
            onSearch={handleSearch}
            onKeyDown={handleSearchKeyDown}
          />

          {/* live car list */}
          <div className={ClassName.liveCarListContainer}>
            <div className={ClassName.liveTitleRow}>
              <span className={ClassName.liveCarListTitle}>{liveCarTitle}</span>
              <span
                className={`${ClassName.liveBadge} ${
                  liveConnected ? ClassName.liveBadgeConnected : ClassName.liveBadgeRefreshing
                }`}
              >
                <span className={ClassName.liveDot} />
                {liveConnected ? liveText : refreshingText}
              </span>
            </div>
            <LiveCarStatusHeader />
            <div className={ClassName.liveCardList}>
              {liveCars.map((car) => (
                <LiveCarCard key={String(car._id)} car={car} />
              ))}
              {!liveCars.length ? (
                <EmptyState
                  title="No active listings"
                  description="Active listings will appear here when they are posted."
                />
              ) : null}
              {liveCars.length > 19 && (
                <LoadMoreButton
                  onClick={handleLoadMore}
                  loading={loadingMore}
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default HomePage;

const ClassName = {
  container: "flex flex-1 flex-col gap-5 text-primaryText lg:flex-row",
  statTitle: "text-lg font-semibold",
  stat: "flex flex-col gap-4 lg:w-80",
  statCards: "flex flex-col sm:flex-row lg:flex-col gap-4",
  liveCarContainer:
    "flex min-w-0 flex-1 flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6",
  callActivity: "rounded-2xl border border-slate-200 bg-slate-50 p-4",
  callTitle: "text-base font-bold text-slate-950",
  callSubtitle: "mt-1 text-xs text-slate-500",
  callGrid: "mt-3 grid gap-2 sm:grid-cols-2",
  liveCarListContainer:
    "flex min-h-0 flex-1 flex-col",
  liveTitleRow: "mb-2 flex items-center justify-between gap-3",
  liveCarListTitle: "text-base font-semibold",
  liveBadge:
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
  liveBadgeConnected: "bg-emerald-50 text-emerald-700",
  liveBadgeRefreshing: "bg-amber-50 text-amber-700",
  liveDot: "h-1.5 w-1.5 rounded-full bg-current",
  liveCardList:
    "flex max-h-[calc(100vh-280px)] min-h-56 flex-col overflow-y-auto pr-1",
};
